import React, { useState, useRef } from "react";
import { TextField } from "@/components/form/fields";
import { validateEmail } from "../validation";
import { supabase } from "@/utils/supabase/client";
import { useOrganization } from '@/context/OrganizationContext';
import { isEmailInUse } from '@/services/athlete/athleteClientService'; // ✅ FIXED: Use client-safe service
import EmailValidationIndicator, { EmailValidationIndicatorRef } from './EmailValidationIndicator';

interface EmailFieldProps {
  id?: string;
  name?: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onValidationChange?: (isValid: boolean, message: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: boolean | string;
  hint?: string;
  className?: string;
  validateOnBlur?: boolean;
  checkDuplicates?: boolean;
  /**
   * Show the email but prevent editing (e.g. locked to an invitation). When set,
   * the duplicate-availability check is skipped — the address is server-issued
   * and must stay exactly as invited, so "already in use" prompts don't apply.
   */
  readOnly?: boolean;
  currentEntityId?: string;
  entityType?: 'athlete' | 'user' | 'staff' | 'parent' | 'athlete_invitation' | 'parent_invitation' | 'organization' | 'gym_location' | 'venue';
  enableRealTimeValidation?: boolean;
  validationTrigger?: 'onChange' | 'onBlur';
  /**
   * #2300: conflict reason codes this form treats as advisory rather than
   * blocking. Matches surface through `onNotice` and leave the field clean.
   * Opt-in — omitting it preserves the historical blocking behavior.
   */
  nonBlockingReasons?: readonly string[];
  /** The server's conflict verdict (blocking or advisory), or null when clean. */
  onNotice?: (notice: { reason: string; message: string; blocking: boolean } | null) => void;
}

const EmailField: React.FC<EmailFieldProps> = ({
  id = "email",
  name = "email",
  value,
  onChange,
  onBlur,
  onValidationChange,
  label = "Email Address",
  placeholder = "name@example.com",
  required = false,
  error = false,
  hint,
  className = "",
  validateOnBlur = true,
  checkDuplicates = false,
  currentEntityId,
  entityType = 'athlete',
  enableRealTimeValidation = true,
  validationTrigger = 'onBlur',
  readOnly = false,
  nonBlockingReasons,
  onNotice,
}) => {
  // Local validation state
  const [localError, setLocalError] = useState<string>("");
  const [touched, setTouched] = useState<boolean>(false);
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState<boolean>(false);
  const { currentOrganization } = useOrganization();
  const validationIndicatorRef = useRef<EmailValidationIndicatorRef>(null);
  
  // Get the actual error to display - prioritize localError (from validation) over parent error
  const displayError = touched ? (localError || (typeof error === 'string' ? error : "")) : "";
  
  // Helper function to check if email looks complete enough for real-time validation
  const isEmailCompleteEnough = (email: string): boolean => {
    // Must have basic structure: something@domain.tld
    const parts = email.split('@');
    if (parts.length !== 2) return false;
    
    const [localPart, domainPart] = parts;
    if (!localPart || !domainPart) return false;
    
    // Domain must have at least one dot
    const domainParts = domainPart.split('.');
    if (domainParts.length < 2) return false;
    
    // Last part (TLD) should be at least 2 characters
    const tld = domainParts[domainParts.length - 1];
    if (tld.length < 2) return false;
    
    // Domain parts shouldn't be empty
    return domainParts.every(part => part.length > 0);
  };
  
  // Function to check for duplicate emails using Supabase
  const checkDuplicateEmail = async (email: string): Promise<boolean> => {
    if (!email || !checkDuplicates) return false;
    if (!currentOrganization) return false;
    
    setIsCheckingDuplicate(true);
    
    try {
      if (entityType === 'athlete') {
        // Use the athlete service for checking email uniqueness
        const isDuplicate = await isEmailInUse(email, currentOrganization.id, currentEntityId);
        return isDuplicate;
      } else if (entityType === 'user') {
        // Check duplicates in users table (or auth_users if that's what you're using)
        const { count, error } = await supabase
          .from('auth_users')
          .select('id', { count: 'exact', head: true })
          .eq('email', email)
          .neq('id', currentEntityId || '');
          
        if (error) {
          console.error("Error checking duplicate user email:", error.message, error.details, error.code);
          return false;
        }
        
        return count !== null && count > 0;
      }
      
      return false;
    } catch (error) {
      // Enhanced error logging
      if (error instanceof Error) {
        console.error("Exception checking for duplicate email:", error.message, error.stack);
      } else {
        console.error("Unknown error checking for duplicate email:", error);
      }
      return false;
    } finally {
      setIsCheckingDuplicate(false);
    }
  };
  
  // Handle direct input changes
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const currentValue = e.target.value;
    
    // Handle validation based on trigger type
    if (validationTrigger === 'onChange') {
      // Complex real-time validation (as user types)
      if (currentValue) {
        const validationResult = validateEmail(currentValue, required);
        
        if (!validationResult.valid) {
          setLocalError(validationResult.message);
          if (onValidationChange) {
            onValidationChange(false, validationResult.message);
          }
        } else {
          setLocalError('');
          if (onValidationChange) {
            onValidationChange(true, '');
          }
        }
      } else if (required) {
        setLocalError('Email address is required');
        if (onValidationChange) {
          onValidationChange(false, 'Email address is required');
        }
      } else {
        setLocalError('');
        if (onValidationChange) {
          onValidationChange(true, '');
        }
      }
    } else {
      // Simple validation for onBlur trigger - just clear errors during typing
      if (currentValue) {
        // Only show basic format errors while typing, clear server validation errors
        const validationResult = validateEmail(currentValue, required);
        if (!validationResult.valid) {
          setLocalError(validationResult.message);
        } else {
          setLocalError('');
        }
      } else if (required) {
        setLocalError('Email address is required');
      } else {
        setLocalError('');
      }
    }
    
    // Always pass through the change
    onChange(e);
  };

  const handleEmailBlur = async (e: React.FocusEvent<HTMLInputElement>) => {
    // Mark as touched
    setTouched(true);
    
    if (validateOnBlur) {
      const currentValue = e.target.value;
      const validationResult = validateEmail(currentValue, required);
      
      // Set immediate validation errors
      setLocalError(validationResult.message);
      
      // Trigger real-time validation on blur if enabled
      if (validationResult.valid && enableRealTimeValidation && checkDuplicates && validationTrigger === 'onBlur') {
        // Check if email is complete enough before triggering
        const parts = currentValue.split('@');
        if (parts.length === 2) {
          const [localPart, domainPart] = parts;
          const domainParts = domainPart.split('.');
          if (domainParts.length >= 2 && domainParts[domainParts.length - 1].length >= 2) {
            // Trigger the EmailValidationIndicator to check for duplicates
            if (validationIndicatorRef.current) {
              console.log('[EmailField] Triggering validation on blur for:', currentValue);
              validationIndicatorRef.current.triggerValidation();
            } else {
              console.warn('[EmailField] validationIndicatorRef.current is null');
            }
          }
        }
      }
      
      if (validationResult.valid && checkDuplicates && validationTrigger === 'onChange') {
        // Legacy duplicate checking for onChange trigger (fallback)
        const isDuplicate = await checkDuplicateEmail(currentValue);
        if (isDuplicate) {
          setLocalError(`This email is already in use by another ${entityType}`);
          if (onValidationChange) {
            onValidationChange(false, `This email is already in use by another ${entityType}`);
          }
        } else {
          if (onValidationChange) {
            onValidationChange(validationResult.valid, validationResult.message);
          }
        }
      } else {
        // Basic validation only
        if (onValidationChange) {
          onValidationChange(validationResult.valid, validationResult.message);
        }
      }
    }
    
    // Call the provided onBlur if exists
    if (onBlur) {
      onBlur(e);
    }
  };

  return (
    <div className={className}>
      <TextField
        id={id}
        name={name}
        label={label}
        type="email"
        placeholder={placeholder}
        value={value}
        onChange={handleEmailChange}
        onBlur={handleEmailBlur}
        error={!!displayError}
        hint={displayError || hint}
        autoComplete="username"
        required={required}
        disabled={isCheckingDuplicate}
        readOnly={readOnly}
      />

      {/* Real-time validation status indicator */}
      <EmailValidationIndicator
        ref={validationIndicatorRef}
        email={value}
        enabled={enableRealTimeValidation && checkDuplicates && !readOnly}
        currentEntityId={currentEntityId}
        entityType={entityType}
        isCheckingDuplicate={isCheckingDuplicate}
        hasError={!!displayError}
        validationTrigger={validationTrigger}
        nonBlockingReasons={nonBlockingReasons}
        onNotice={onNotice}
        onValidationChange={(isValid, message) => {
          if (!isValid && message) {
            setLocalError(message);
            setTouched(true);
          } else {
            setLocalError('');
          }
          if (onValidationChange) {
            onValidationChange(isValid, message);
          }
        }}
      />
      
      {/* Legacy loading message for backward compatibility */}
      {!enableRealTimeValidation && isCheckingDuplicate && (
        <p className="mt-1 text-xs text-gray-500">Checking email availability...</p>
      )}
      {!enableRealTimeValidation && !displayError && !isCheckingDuplicate && value && touched && (
        <p className="mt-1 text-xs text-success-500">Email format is valid</p>
      )}
    </div>
  );
};

export default EmailField; 